<template>
  <div class="linkedin-activities-tab">
    <div class="activities-tabs">
      <button
        :class="['tab-button', { active: activeTab === 'posts' }]"
        @click="switchTab('posts')"
      >
        Posts
      </button>
      <button
        :class="['tab-button', { active: activeTab === 'comments' }]"
        @click="switchTab('comments')"
      >
        Comments
      </button>
      <button
        :class="['tab-button', { active: activeTab === 'reactions' }]"
        @click="switchTab('reactions')"
      >
        Reactions
      </button>
    </div>

    <div class="activities-content">
      <div v-if="error && errorType !== 'not-found'" class="error-state">
        <p class="error-message">
          {{
            activeTab === "posts"
              ? "Couldn't load posts for this profile"
              : activeTab === "comments"
                ? "Couldn't load comments for this profile"
                : "Couldn't load reactions for this profile"
          }}
        </p>
        <Button variant="primary" size="sm" @click="retryCurrentTab">Try Again</Button>
      </div>

      <div v-else-if="isLoading && currentItems.length === 0" class="loading-state">
        <LoadingBars size="sm" />
      </div>

      <div v-else-if="!isLoading && currentItems.length === 0" class="empty-state">
        <p v-if="activeTab === 'posts'">No posts found for this profile</p>
        <p v-else-if="activeTab === 'comments'">No comments found for this profile</p>
        <p v-else>No reactions found for this profile</p>
      </div>

      <div v-else-if="activeTab === 'posts'" class="posts-content">
        <div
          v-for="(post, index) in postsState.items"
          :key="post.id || index"
          class="activity-card post-card"
        >
          <div v-if="post.is_repost && post.reposted_by" class="repost-header">
            <Icon icon="mdi:repeat" class="repost-icon" />
            <span class="repost-label"> {{ post.reposted_by.name }} reposted </span>
          </div>

          <div class="post-header">
            <div class="post-author">
              <strong>{{ post.author?.name || post.written_by?.name || "Unknown" }}</strong>
              <span v-if="post.author?.headline" class="author-headline">
                {{ post.author.headline }}
              </span>
              <span v-if="post.author?.is_company" class="company-badge">Company</span>
            </div>
            <span class="post-date">
              <Icon icon="formkit:datetime" class="post-date-icon" />
              {{ formatDate(post.parsed_datetime || post.date) }}
            </span>
          </div>

          <h4 v-if="post.title" class="post-title">{{ post.title }}</h4>

          <p v-if="post.text" class="activity-text">{{ post.text }}</p>

          <div v-if="post.is_repost && post.repost_content" class="repost-content">
            <div class="repost-content-header">
              <strong>{{ post.repost_content.author?.name || "Unknown" }}</strong>
              <span class="repost-content-date">
                <Icon icon="formkit:datetime" class="post-date-icon" />
                {{
                  formatDate(
                    post.repost_content.parsed_datetime || post.repost_content.date || undefined
                  )
                }}
              </span>
            </div>
            <p v-if="post.repost_content.text" class="repost-content-text">
              {{ post.repost_content.text }}
            </p>
          </div>

          <div v-if="post.attachments && post.attachments.length > 0" class="post-attachments">
            <AttachmentPreview
              v-for="attachment in post.attachments"
              :key="attachment.id"
              :attachment="attachment"
              class="post-attachment"
            />
          </div>

          <div v-else-if="post.images && post.images.length > 0" class="activity-media">
            <div class="image-grid">
              <img
                v-for="(img, imgIndex) in post.images.slice(0, 4)"
                :key="imgIndex"
                :src="img"
                alt="Post image"
                class="post-image"
              />
            </div>
          </div>

          <div v-if="post.poll" class="post-poll">
            <h5 class="poll-question">{{ post.poll.question }}</h5>
            <div class="poll-options">
              <div
                v-for="option in post.poll.options"
                :key="option.id"
                class="poll-option"
                :class="{ 'poll-option--winning': option.win }"
              >
                <div class="poll-option-header">
                  <span class="poll-option-text">{{ option.text }}</span>
                  <span class="poll-option-votes">{{ option.votes_count || 0 }} votes</span>
                </div>
                <div
                  v-if="post.poll.total_votes_count && post.poll.total_votes_count > 0"
                  class="poll-option-bar"
                  :style="{
                    width: `${((option.votes_count || 0) / (post.poll.total_votes_count || 1)) * 100}%`,
                  }"
                ></div>
              </div>
            </div>
            <div class="poll-footer">
              <span class="poll-total-votes">
                {{ post.poll.total_votes_count || 0 }} total votes
              </span>
              <span v-if="post.poll.is_open" class="poll-status">Open</span>
              <span v-else class="poll-status poll-status--closed">Closed</span>
            </div>
          </div>

          <div v-if="post.group" class="post-group">
            <Icon icon="mdi:account-group" />
            <span>{{ post.group.name }}</span>
            <span v-if="post.group.private" class="group-private">Private</span>
          </div>

          <div class="activity-engagement">
            <div class="engagement-metrics">
              <span
                v-if="post.reaction_counter !== undefined && post.reaction_counter > 0"
                class="metric-item"
              >
                <Icon icon="ei:like" />
                {{ formatNumber(post.reaction_counter) }}
              </span>
              <span
                v-if="post.comment_counter !== undefined && post.comment_counter > 0"
                class="metric-item"
              >
                <Icon icon="proicons:comment" />
                {{ formatNumber(post.comment_counter) }}
              </span>
              <span
                v-if="
                  (post.repost_counter !== undefined || post.share_counter !== undefined) &&
                  (post.repost_counter || post.share_counter || 0) > 0
                "
                class="metric-item"
              >
                <Icon icon="bx:repost" />
                {{ formatNumber(post.repost_counter || post.share_counter || 0) }}
              </span>
              <span
                v-if="post.impressions_counter !== undefined && post.impressions_counter > 0"
                class="metric-item"
              >
                <Icon icon="nrk:some-share" />
                {{ formatNumber(post.impressions_counter) }}
              </span>
            </div>
            <a
              v-if="post.share_url"
              :href="post.share_url"
              target="_blank"
              rel="noopener noreferrer"
              class="view-link"
            >
              <Icon icon="mdi:open-in-new" />
              View on LinkedIn
            </a>
          </div>

          <div v-if="post.user_reacted" class="user-reaction">
            <Icon :icon="getReactionIcon(post.user_reacted)" />
            <span>You {{ post.user_reacted.toLowerCase() }}d this</span>
          </div>

          <div v-if="post.analytics" class="post-analytics">
            <details class="analytics-details">
              <summary class="analytics-summary">
                <Icon icon="mdi:chart-line" />
                View Analytics
              </summary>
              <div class="analytics-content">
                <div class="analytics-item">
                  <span class="analytics-label">Impressions:</span>
                  <span class="analytics-value">{{
                    formatNumber(post.analytics.impressions || 0)
                  }}</span>
                </div>
                <div class="analytics-item">
                  <span class="analytics-label">Engagements:</span>
                  <span class="analytics-value">{{
                    formatNumber(post.analytics.engagements || 0)
                  }}</span>
                </div>
                <div class="analytics-item">
                  <span class="analytics-label">Engagement Rate:</span>
                  <span class="analytics-value">
                    {{ (post.analytics.engagement_rate || 0).toFixed(2) }}%
                  </span>
                </div>
                <div v-if="post.analytics.clicks" class="analytics-item">
                  <span class="analytics-label">Clicks:</span>
                  <span class="analytics-value">{{ formatNumber(post.analytics.clicks) }}</span>
                </div>
                <div v-if="post.analytics.clickthrough_rate" class="analytics-item">
                  <span class="analytics-label">CTR:</span>
                  <span class="analytics-value">
                    {{ post.analytics.clickthrough_rate.toFixed(2) }}%
                  </span>
                </div>
              </div>
            </details>
          </div>
        </div>

        <Button
          v-if="postsState.cursor"
          variant="secondary"
          size="md"
          :loading="isLoadingMore"
          :disabled="isLoadingMore"
          block
          @click="loadMorePosts"
        >
          Load More
        </Button>
      </div>

      <div v-else-if="activeTab === 'comments'" class="comments-content">
        <div
          v-for="(comment, index) in commentsState.items"
          :key="comment.id || index"
          class="activity-card comment-card"
        >
          <div class="comment-header">
            <div class="comment-author">
              <div class="author-info">
                <strong>{{ comment.author || comment.author_details?.id || "Unknown" }}</strong>
                <div class="author-details">
                  <span v-if="comment.author_details?.headline" class="author-headline">{{
                    comment.author_details.headline
                  }}</span>
                  <span v-if="comment.author_details?.network_distance" class="network-distance">
                    {{ formatNetworkDistance(comment.author_details.network_distance) }}
                  </span>
                </div>
              </div>
            </div>
            <span class="comment-date">{{ formatDate(comment.date) }}</span>
          </div>

          <div class="comment-text">{{ comment.text || "" }}</div>

          <div class="comment-engagement">
            <div class="engagement-metrics">
              <span
                v-if="comment.reaction_counter !== undefined && comment.reaction_counter > 0"
                class="metric-item"
              >
                <Icon icon="ei:like" />
                {{ formatNumber(comment.reaction_counter) }}
              </span>
              <span
                v-if="comment.reply_counter !== undefined && comment.reply_counter > 0"
                class="metric-item"
              >
                <Icon icon="mdi:reply-outline" />
                {{ formatNumber(comment.reply_counter) }}
              </span>
              <span
                v-if="comment.impressions_counter !== undefined && comment.impressions_counter > 0"
                class="metric-item"
              >
                <Icon icon="nrk:some-share" />
                {{ formatNumber(comment.impressions_counter) }}
              </span>
            </div>
            <a
              v-if="comment.post_id"
              :href="`https://www.linkedin.com/feed/update/${comment.post_id}`"
              target="_blank"
              rel="noopener noreferrer"
              class="view-link"
            >
              <Icon icon="mdi:open-in-new" />
              View Post
            </a>
          </div>

          <div v-if="comment.user_reacted" class="user-reaction">
            <Icon :icon="getReactionIcon(comment.user_reacted)" />
            <span>You {{ comment.user_reacted.toLowerCase() }}d this</span>
          </div>
        </div>

        <Button
          v-if="commentsState.cursor"
          variant="secondary"
          size="md"
          :loading="isLoadingMore"
          :disabled="isLoadingMore"
          block
          @click="loadMoreComments"
        >
          Load More
        </Button>
      </div>

      <div v-else-if="activeTab === 'reactions'" class="reactions-content">
        <div
          v-for="(reaction, index) in reactionsState.items"
          :key="reaction.post_id || reaction.comment_id || index"
          class="activity-card reaction-card"
        >
          <div class="reaction-header">
            <div class="reaction-author">
              <div class="author-info">
                <strong>{{ reaction.author?.name || "Unknown" }}</strong>
                <div class="author-details">
                  <span v-if="reaction.author?.headline" class="author-headline">{{
                    reaction.author.headline
                  }}</span>
                  <span v-if="reaction.author?.network_distance" class="network-distance">
                    {{ formatNetworkDistance(reaction.author.network_distance) }}
                  </span>
                </div>
              </div>
            </div>
            <div class="reaction-type-badge">
              <Icon :icon="getReactionIcon(reaction.value || 'LIKE')" class="reaction-icon" />
              <span class="reaction-type-text">{{ formatReactionType(reaction.value) }}</span>
            </div>
          </div>

          <div class="reaction-content">
            <div v-if="reaction.post_id" class="reaction-target">
              <Icon icon="mdi:file-document-outline" class="target-icon" />
              <span class="reaction-target-text">Reacted to a post</span>
              <a
                :href="`https://www.linkedin.com/feed/update/${reaction.post_id}`"
                target="_blank"
                rel="noopener noreferrer"
                class="reaction-link"
              >
                View Post
                <Icon icon="mdi:open-in-new" class="link-icon" />
              </a>
            </div>
            <div v-else-if="reaction.comment_id" class="reaction-target">
              <Icon icon="proicons:comment" class="target-icon" />
              <span class="reaction-target-text">Reacted to a comment</span>
              <a
                v-if="reaction.post_id"
                :href="`https://www.linkedin.com/feed/update/${reaction.post_id}`"
                target="_blank"
                rel="noopener noreferrer"
                class="reaction-link"
              >
                View Post
                <Icon icon="mdi:open-in-new" class="link-icon" />
              </a>
              <span v-else class="reaction-link" style="color: #9ca3af; cursor: default">
                Comment ID: {{ reaction.comment_id }}
              </span>
            </div>
          </div>
        </div>

        <Button
          v-if="reactionsState.cursor"
          variant="secondary"
          size="md"
          :loading="isLoadingMore"
          :disabled="isLoadingMore"
          block
          @click="loadMoreReactions"
        >
          Load More
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Comment, Post, PostReaction } from "@/dtos/Unipile.dto";
import Unipile from "@/services/unipile/unipile";
import Button from "@/components/ui/Button.vue";
import AttachmentPreview from "@/components/socials/AttachmentPreview.vue";
import { Icon } from "@iconify/vue";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import { computed, onMounted, ref, watch } from "vue";

interface Props {
  accountId?: string;
  identifier?: string;
  followers?: number;
  cache?: Record<string, any>;
}

const props = withDefaults(defineProps<Props>(), {
  accountId: undefined,
  identifier: undefined,
  followers: 0,
  cache: () => ({}),
});

const emit = defineEmits<{
  "cache-update": [data: { posts: any; comments: any; reactions: any }];
}>();

type TabType = "posts" | "comments" | "reactions";

interface TabState<T> {
  items: T[];
  cursor: string | null;
  loading: boolean;
  error: string | null;
}

const activeTab = ref<TabType>("posts");
const postsState = ref<TabState<Post>>({
  items: [],
  cursor: null,
  loading: false,
  error: null,
});
const commentsState = ref<TabState<Comment>>({
  items: [],
  cursor: null,
  loading: false,
  error: null,
});
const reactionsState = ref<TabState<PostReaction>>({
  items: [],
  cursor: null,
  loading: false,
  error: null,
});

const error = ref<string | null>(null);
const errorType = ref<"not-found" | "generic" | null>(null);

const formattedFollowers = computed(() => {
  return (props.followers || 0).toLocaleString();
});

const currentItems = computed(() => {
  if (activeTab.value === "posts") return postsState.value.items;
  if (activeTab.value === "comments") return commentsState.value.items;
  if (activeTab.value === "reactions") return reactionsState.value.items;
  return [];
});

const isLoading = computed(() => {
  if (activeTab.value === "posts")
    return postsState.value.loading && postsState.value.items.length === 0;
  if (activeTab.value === "comments")
    return commentsState.value.loading && commentsState.value.items.length === 0;
  if (activeTab.value === "reactions")
    return reactionsState.value.loading && reactionsState.value.items.length === 0;
  return false;
});

const isLoadingMore = computed(() => {
  if (activeTab.value === "posts")
    return postsState.value.loading && postsState.value.items.length > 0;
  if (activeTab.value === "comments")
    return commentsState.value.loading && commentsState.value.items.length > 0;
  if (activeTab.value === "reactions")
    return reactionsState.value.loading && reactionsState.value.items.length > 0;
  return false;
});

const formatDate = (dateString?: string): string => {
  if (!dateString) return "";
  if (/^\d+\s*(yr|yrs|y|mo|w|d|h)$/i.test(dateString.trim())) {
    return dateString.trim();
  }

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    const diffWeeks = Math.floor(diffDays / 7);
    if (diffDays < 30) return `${Math.max(1, diffWeeks)}w`;
    const diffMonths = Math.floor(diffDays / 30);
    if (diffDays < 365) return `${Math.max(1, diffMonths)}mo`;
    const diffYears = Math.floor(diffDays / 365);
    return `${Math.max(1, diffYears)}y`;
  } catch {
    return dateString;
  }
};

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toString();
};

const getReactionIcon = (reaction: string): string => {
  const iconMap: Record<string, string> = {
    LIKE: "ei:like",
    LOVE: "mdi:heart",
    PRAISE: "mdi:hand-heart",
    APPRECIATION: "mdi:hand-heart",
    EMPATHY: "mdi:heart-outline",
    INTEREST: "mdi:lightbulb",
    ENTERTAINMENT: "mdi:emoticon-lol",
    CELEBRATE: "mdi:party-popper",
    SUPPORT: "mdi:hand-heart",
    FUNNY: "mdi:emoticon-lol",
    INSIGHTFUL: "mdi:lightbulb",
    CURIOUS: "mdi:help-circle",
  };
  return iconMap[reaction] || "ei:like";
};

const formatReactionType = (reaction?: string): string => {
  if (!reaction) return "Like";
  const typeMap: Record<string, string> = {
    LIKE: "Like",
    LOVE: "Love",
    PRAISE: "Praise",
    APPRECIATION: "Appreciation",
    EMPATHY: "Empathy",
    INTEREST: "Interest",
    ENTERTAINMENT: "Entertainment",
    CELEBRATE: "Celebrate",
    SUPPORT: "Support",
    FUNNY: "Funny",
    INSIGHTFUL: "Insightful",
    CURIOUS: "Curious",
  };
  return typeMap[reaction] || reaction;
};

const isLinkedInCompany = (identifier: string): boolean => {
  return /^[0-9]+$/.test(identifier);
};

const formatNetworkDistance = (distance?: string): string => {
  if (!distance) return "";
  const distanceMap: Record<string, string> = {
    FIRST_DEGREE: "1st connection",
    SECOND_DEGREE: "2nd connection",
    THIRD_DEGREE: "3rd connection",
  };
  return distanceMap[distance] || distance;
};

const handleError = (err: any, tab: TabType) => {
  console.error(`Failed to load ${tab}:`, err);

  const status = err.response?.status;
  const errorData = err.response?.data;

  const getErrorMessage = () => {
    if (errorData?.providerError?.detail) {
      return errorData.providerError.detail;
    }
    if (errorData?.error?.body?.detail) {
      return errorData.error.body.detail;
    }
    if (errorData?.errorMessage) {
      return errorData.errorMessage;
    }
    if (errorData?.message) {
      return errorData.message;
    }
    if (errorData?.providerError?.title) {
      return errorData.providerError.title;
    }
    if (err.message) {
      return err.message;
    }
    return null;
  };

  const extractedMessage = getErrorMessage();
  let userMessage = `Failed to load ${tab}.`;

  switch (status) {
    case 400:
      if (
        extractedMessage?.toLowerCase().includes("identifier") &&
        extractedMessage?.toLowerCase().includes("required")
      ) {
        userMessage = "LinkedIn profile identifier is required.";
      } else if (
        extractedMessage?.toLowerCase().includes("account_id") &&
        extractedMessage?.toLowerCase().includes("required")
      ) {
        userMessage = "LinkedIn account ID is required.";
      } else if (extractedMessage?.toLowerCase().includes("limit")) {
        userMessage = "Invalid limit. Please use a value between 1 and 100.";
      } else if (extractedMessage?.toLowerCase().includes("cursor")) {
        userMessage = "Invalid cursor for pagination.";
      } else {
        userMessage = extractedMessage || "Invalid request. Please check your parameters.";
      }
      errorType.value = "generic";
      break;
    case 401:
      if (extractedMessage?.toLowerCase().includes("disconnected")) {
        userMessage =
          "Your LinkedIn account appears to be disconnected. Please reconnect your account.";
      } else {
        userMessage = extractedMessage || "Unauthorized. Please check your credentials.";
      }
      errorType.value = "generic";
      break;
    case 403:
      userMessage =
        extractedMessage || "This feature is not available for your subscription or account type.";
      errorType.value = "generic";
      break;
    case 404:
      if (extractedMessage?.toLowerCase().includes("account not found")) {
        userMessage = "LinkedIn account not found. Please check your account settings.";
      } else {
        userMessage =
          extractedMessage || "Account or user not found. The requested resource was not found.";
      }
      errorType.value = "not-found";
      break;
    case 422:
      if (extractedMessage?.toLowerCase().includes("cannot be reached")) {
        userMessage =
          "Recipient cannot be reached. Make sure the identifier is valid and the profile is not locked.";
      } else {
        userMessage =
          extractedMessage ||
          "Unable to process request. Please check the identifier and try again.";
      }
      errorType.value = "generic";
      break;
    case 429:
      userMessage = extractedMessage || "Too many requests. Please wait a moment and try again.";
      errorType.value = "generic";
      break;
    case 502:
      if (extractedMessage?.toLowerCase().includes("account not found")) {
        userMessage = "LinkedIn account not found. Please check your account settings.";
      } else {
        userMessage =
          extractedMessage ||
          "LinkedIn service is temporarily unavailable. Please try again later.";
      }
      errorType.value = "generic";
      break;
    case 500:
    case 503:
    case 504:
      userMessage =
        extractedMessage || "LinkedIn service is temporarily unavailable. Please try again later.";
      errorType.value = "generic";
      break;
    default:
      userMessage = extractedMessage || "An unexpected error occurred. Please try again.";
      errorType.value = "generic";
  }

  error.value = userMessage;
};

const getCacheKey = () => {
  if (!props.identifier || !props.accountId) return null;
  return `${props.identifier}-${props.accountId}`;
};

const loadPosts = async (cursor?: string) => {
  if (!props.identifier) {
    error.value = "LinkedIn profile identifier is required to load posts.";
    errorType.value = "generic";
    return;
  }

  if (!props.accountId) {
    error.value = "LinkedIn account ID is required to load posts.";
    errorType.value = "generic";
    return;
  }

  const cacheKey = getCacheKey();
  if (!cursor && cacheKey && props.cache?.[cacheKey]?.posts) {
    const cached = props.cache[cacheKey].posts;
    postsState.value.items = cached.items || [];
    postsState.value.cursor = cached.cursor || null;
    return;
  }

  try {
    error.value = null;
    errorType.value = null;
    postsState.value.loading = true;

    const params: any = {
      limit: 25,
      account_id: props.accountId,
    };

    if (cursor) {
      params.cursor = cursor;
    }

    const isCompany = isLinkedInCompany(props.identifier);
    if (isCompany) {
      params.is_company = true;
    }

    const response = await Unipile.getAllPosts(props.identifier, params);
    const data = response.data.data;

    if (cursor) {
      postsState.value.items = [...postsState.value.items, ...(data.items || [])];
    } else {
      postsState.value.items = data.items || [];
    }

    postsState.value.cursor = data.cursor || null;

    // Update cache
    if (cacheKey && !cursor) {
      emit("cache-update", {
        posts: { items: postsState.value.items, cursor: postsState.value.cursor },
        comments: props.cache?.[cacheKey]?.comments || null,
        reactions: props.cache?.[cacheKey]?.reactions || null,
      });
    }
  } catch (err: any) {
    handleError(err, "posts");
  } finally {
    postsState.value.loading = false;
  }
};

const loadComments = async (cursor?: string) => {
  if (!props.identifier) {
    error.value = "LinkedIn profile identifier is required to load comments.";
    errorType.value = "generic";
    return;
  }

  if (!props.accountId) {
    error.value = "LinkedIn account ID is required to load comments.";
    errorType.value = "generic";
    return;
  }

  const cacheKey = getCacheKey();
  if (!cursor && cacheKey && props.cache?.[cacheKey]?.comments) {
    const cached = props.cache[cacheKey].comments;
    commentsState.value.items = cached.items || [];
    commentsState.value.cursor = cached.cursor || null;
    return;
  }

  try {
    error.value = null;
    errorType.value = null;
    commentsState.value.loading = true;

    const params: any = {
      limit: 25,
      account_id: props.accountId,
    };

    if (cursor) {
      params.cursor = cursor;
    }

    const response = await Unipile.getAllComments(props.identifier, params);
    const data = response.data.data;

    if (cursor) {
      commentsState.value.items = [...commentsState.value.items, ...(data.items || [])];
    } else {
      commentsState.value.items = data.items || [];
    }

    commentsState.value.cursor = data.cursor || null;
    if (cacheKey && !cursor) {
      emit("cache-update", {
        posts: props.cache?.[cacheKey]?.posts || null,
        comments: { items: commentsState.value.items, cursor: commentsState.value.cursor },
        reactions: props.cache?.[cacheKey]?.reactions || null,
      });
    }
  } catch (err: any) {
    handleError(err, "comments");
  } finally {
    commentsState.value.loading = false;
  }
};

const loadMorePosts = () => {
  if (postsState.value.cursor) {
    loadPosts(postsState.value.cursor);
  }
};

const loadMoreComments = () => {
  if (commentsState.value.cursor) {
    loadComments(commentsState.value.cursor);
  }
};

const loadReactions = async (cursor?: string) => {
  if (!props.identifier) {
    error.value = "LinkedIn profile identifier is required to load reactions.";
    errorType.value = "generic";
    return;
  }

  if (!props.accountId) {
    error.value = "LinkedIn account ID is required to load reactions.";
    errorType.value = "generic";
    return;
  }

  const cacheKey = getCacheKey();
  if (!cursor && cacheKey && props.cache?.[cacheKey]?.reactions) {
    const cached = props.cache[cacheKey].reactions;
    reactionsState.value.items = cached.items || [];
    reactionsState.value.cursor = cached.cursor || null;
    return;
  }

  try {
    error.value = null;
    errorType.value = null;
    reactionsState.value.loading = true;

    const params: any = {
      limit: 25,
      account_id: props.accountId,
    };

    if (cursor) {
      params.cursor = cursor;
    }

    const response = await Unipile.getAllReactions(props.identifier, params);
    const data = response.data.data;

    if (cursor) {
      reactionsState.value.items = [...reactionsState.value.items, ...(data.items || [])];
    } else {
      reactionsState.value.items = data.items || [];
    }

    reactionsState.value.cursor = data.cursor || null;
    if (cacheKey && !cursor) {
      emit("cache-update", {
        posts: props.cache?.[cacheKey]?.posts || null,
        comments: props.cache?.[cacheKey]?.comments || null,
        reactions: { items: reactionsState.value.items, cursor: reactionsState.value.cursor },
      });
    }
  } catch (err: any) {
    handleError(err, "reactions");
  } finally {
    reactionsState.value.loading = false;
  }
};

const loadMoreReactions = () => {
  if (reactionsState.value.cursor) {
    loadReactions(reactionsState.value.cursor);
  }
};

const switchTab = (tab: TabType) => {
  if (activeTab.value === tab) return;

  activeTab.value = tab;
  error.value = null;
  errorType.value = null;

  if (tab === "posts" && postsState.value.items.length === 0 && !postsState.value.loading) {
    loadPosts();
  } else if (
    tab === "comments" &&
    commentsState.value.items.length === 0 &&
    !commentsState.value.loading
  ) {
    loadComments();
  } else if (
    tab === "reactions" &&
    reactionsState.value.items.length === 0 &&
    !reactionsState.value.loading
  ) {
    loadReactions();
  }
};

const retryCurrentTab = () => {
  error.value = null;
  errorType.value = null;

  if (activeTab.value === "posts") {
    postsState.value.items = [];
    postsState.value.cursor = null;
    loadPosts();
  } else if (activeTab.value === "comments") {
    commentsState.value.items = [];
    commentsState.value.cursor = null;
    loadComments();
  } else if (activeTab.value === "reactions") {
    reactionsState.value.items = [];
    reactionsState.value.cursor = null;
    loadReactions();
  }
};

onMounted(() => {
  if (props.identifier) {
    loadPosts();
  } else {
    error.value = "No profile identifier provided.";
    errorType.value = "generic";
  }
});
</script>

<style scoped>
.linkedin-activities-tab {
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 8px 6px;
  max-height: 560px;
  overflow-y: auto;
}

.activities-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.tab-button {
  padding: 6px 14px;
  border-radius: 24px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #d0d5dd;
  background: #ffffff;
  color: #646464;
  white-space: nowrap;
  line-height: 1.5;
}

.tab-button:hover:not(.active) {
  background: #f9fafb;
  border-color: #9ca3af;
}

.tab-button.active {
  background: #10b981;
  color: #ffffff;
  border-color: #10b981;
  box-shadow: 0 1px 2px rgba(16, 185, 129, 0.1);
}

.tab-button.active:hover {
  background: #059669;
  border-color: #059669;
}

.activities-content {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

.posts-content,
.comments-content,
.reactions-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0;
  margin: 0;
  color: #4b5563;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 12px;
  color: #9ca3af;
}

.loading-icon {
  font-size: 1.5rem;
  animation: spin 1s linear infinite;
  margin-bottom: 0.75rem;
  color: #10b981;
}

.loading-state p {
  font-size: 0.875rem;
  margin: 0;
}

.loading-icon-small {
  font-size: 1rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.empty-state {
  text-align: center;
  padding: 48px 24px;
  color: #6b7280;
}

.empty-state p {
  font-size: 0.875rem;
  margin: 0;
  color: #6b7280;
}

.error-state {
  text-align: center;
  padding: 48px 24px;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.error-message {
  margin: 0;
  font-size: 0.875rem;
  color: #6b7280;
  max-width: 400px;
}

.activity-card {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 14px 0;
  box-shadow: none;
}

.activity-row {
  border-bottom: 1px solid #e5e7eb;
}

.activity-row:last-child {
  border-bottom: none;
}

.activity-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 10px;
}

.activity-meta__name {
  font-weight: 700;
  color: #374151;
}

.activity-meta__dot {
  color: #9ca3af;
}

.activity-body {
  font-size: 0.9rem;
  line-height: 1.5;
  color: #111827;
  white-space: pre-wrap;
}

.activity-text {
  font-size: 0.875rem;
  line-height: 1.5;
  color: #4b5563;
  white-space: pre-wrap;
  margin-bottom: 10px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  overflow: hidden;
  text-overflow: ellipsis;
}

.post-header,
.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 12px;
}

.post-author,
.comment-author-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.post-author strong,
.comment-author {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a1a2e;
}

.author-headline {
  font-size: 0.75rem;
  color: #6b7280;
}

.post-date,
.comment-date {
  font-size: 0.75rem;
  color: #9ca3af;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.post-date-icon {
  font-size: 0.9rem;
}

.activity-media {
  margin-bottom: 12px;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
  max-height: 300px;
  overflow: hidden;
}

.post-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
  background: #f3f4f6;
}

.activity-engagement {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #f3f4f6;
}

.engagement-metrics {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.metric-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.875rem;
  color: #646464;
}

.metric-item :deep(svg) {
  font-size: 1rem;
}

.view-link {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: #0a66c2;
  text-decoration: none;
  font-weight: 600;
  white-space: nowrap;
}

.view-link:hover {
  text-decoration: underline;
}

.post-card,
.comment-card,
.reaction-card {
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 16px;
  margin-top: 0;
}

.reaction-card:first-child {
  padding-top: 0;
  margin-top: 0;
}

.post-card:last-child,
.comment-card:last-child,
.reaction-card:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 12px;
}

.comment-author {
  flex: 1;
}

.author-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.author-info strong {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a1a2e;
}

.author-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.network-distance {
  font-size: 0.7rem;
  color: #9ca3af;
  font-weight: 500;
}

.comment-text {
  font-size: 0.875rem;
  line-height: 1.5;
  color: #4b5563;
  white-space: pre-wrap;
  margin-bottom: 10px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
  line-clamp: 5;
  overflow: hidden;
  text-overflow: ellipsis;
}

.comment-engagement {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #f3f4f6;
}

.repost-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: #f3f4f6;
  border-radius: 6px;
  font-size: 0.8125rem;
  color: #6b7280;
}

.repost-icon {
  font-size: 1rem;
  color: #10b981;
}

.repost-label {
  font-weight: 500;
}

.post-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 8px 0;
}

.company-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #e0e7ff;
  color: #4338ca;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  margin-left: 6px;
}

.repost-content {
  margin: 12px 0;
  padding: 12px;
  background: #f9fafb;
  border-left: 3px solid #10b981;
  border-radius: 6px;
}

.repost-content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.repost-content-header strong {
  font-size: 0.875rem;
  color: #1a1a2e;
}

.repost-content-date {
  font-size: 0.75rem;
  color: #9ca3af;
}

.repost-content-text {
  font-size: 0.875rem;
  color: #4c4f6a;
  line-height: 1.5;
  margin: 0;
}

.post-attachments {
  margin: 12px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.post-attachment {
  border-radius: 8px;
  overflow: hidden;
}

.post-poll {
  margin: 12px 0;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.poll-question {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 12px 0;
}

.poll-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}

.poll-option {
  padding: 8px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  position: relative;
  overflow: hidden;
}

.poll-option--winning {
  border-color: #10b981;
  background: #f0fdf4;
}

.poll-option-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  position: relative;
  z-index: 1;
}

.poll-option-text {
  font-size: 0.875rem;
  color: #1a1a2e;
  font-weight: 500;
}

.poll-option-votes {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 600;
}

.poll-option-bar {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: #10b981;
  opacity: 0.15;
  transition: width 0.3s ease;
}

.poll-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
  font-size: 0.75rem;
  color: #6b7280;
}

.poll-status {
  font-weight: 600;
  color: #10b981;
}

.poll-status--closed {
  color: #9ca3af;
}

.post-group {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 12px 0;
  padding: 8px 12px;
  background: #f3f4f6;
  border-radius: 6px;
  font-size: 0.8125rem;
  color: #6b7280;
}

.group-private {
  margin-left: auto;
  padding: 2px 6px;
  background: #fee2e2;
  color: #991b1b;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
}

.user-reaction {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 6px 12px;
  background: #eff6ff;
  border-radius: 6px;
  font-size: 0.8125rem;
  color: #1e40af;
  font-weight: 500;
}

.post-analytics {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
}

.analytics-details {
  cursor: pointer;
}

.analytics-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  color: #3e3efe;
  font-weight: 500;
  list-style: none;
}

.analytics-summary::-webkit-details-marker {
  display: none;
}

.analytics-summary::marker {
  display: none;
}

.analytics-content {
  margin-top: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.analytics-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8125rem;
}

.analytics-label {
  color: #6b7280;
}

.analytics-value {
  font-weight: 600;
  color: #1a1a2e;
}

.reaction-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 12px;
}

.reaction-author {
  flex: 1;
}

.reaction-type-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #f0fdf4;
  border: 1px solid #10b981;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #059669;
  white-space: nowrap;
}

.reaction-icon {
  font-size: 0.875rem;
  color: #10b981;
}

.reaction-type-text {
  text-transform: capitalize;
}

.reaction-content {
  margin-top: 8px;
}

.reaction-target {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  color: #6b7280;
  padding: 8px 12px;
  background: #f9fafb;
  border-radius: 6px;
}

.target-icon {
  font-size: 0.875rem;
  color: #9ca3af;
  flex-shrink: 0;
  order: -1;
}

.reaction-target-text {
  flex: 1;
}

.reaction-link {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  color: #10b981;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.75rem;
  transition: color 0.2s ease;
}

.reaction-link:hover {
  color: #059669;
  text-decoration: underline;
}

.link-icon {
  font-size: 0.75rem;
}
</style>
